import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function ReportsGeneral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchReport();
  }, [genderFilter]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/reports/general', {
        params: { gender: genderFilter || undefined },
      });
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Reports" />
        <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5 }}>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Box sx={{ height: 14, width: '60%', borderRadius: 1, bgcolor: '#EDF1EE' }} />
            <Box sx={{ height: 14, width: '85%', borderRadius: 1, bgcolor: '#EDF1EE' }} />
            <Box sx={{ height: 14, width: '72%', borderRadius: 1, bgcolor: '#EDF1EE' }} />
          </Box>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Reports" />
        <Alert severity="error" sx={{ borderRadius: '9px', bgcolor: tokens.dangerSoft, color: '#7C2E1D', border: '1px solid #EFCFC5' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const reportCards = [
    {
      title: 'Membership by region',
      desc: 'Counts, growth and status by region and branch.',
      path: '/dashboard/membership-report',
    },
    {
      title: 'Dues collection / financial',
      desc: 'Income, expenditure and net balance by year.',
      path: '/dashboard/financial-report',
    },
    {
      title: 'General membership',
      desc: 'Gender distribution and registry overview.',
      path: '/dashboard/general-report',
      current: true,
    },
    {
      title: 'Events data',
      desc: 'Participation and attendance by event.',
      path: '/dashboard/reports/events-data',
    },
  ];

  return (
    <Box>
      <PageHeader
        crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>}
        title="Reports"
        actions={
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Gender</InputLabel>
            <Select
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              label="Filter by Gender"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </Select>
          </FormControl>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {reportCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5, height: '100%' }}>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 14.5, mb: 0.75, color: tokens.ink }}>
                {card.title}
              </Typography>
              <Typography sx={{ color: tokens.muted, fontSize: 13, mb: 1.75, minHeight: 40 }}>
                {card.desc}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => !card.current && navigate(card.path)}
                sx={{ borderColor: tokens.border, color: tokens.ink }}
              >
                {card.current ? 'Viewing' : 'Open'}
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={surfaceCardSx}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.border}` }}>
          <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 15 }}>
            Gender distribution
          </Typography>
        </Box>
        <Box sx={{ p: 2.5, display: 'grid', gap: 1.25 }}>
          <Typography sx={{ fontSize: 13.5 }}>
            Male:{' '}
            <Box component="b" className="num" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {data?.gender?.male || 0}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 13.5 }}>
            Female:{' '}
            <Box component="b" className="num" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {data?.gender?.female || 0}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 13.5 }}>
            Total:{' '}
            <Box component="b" className="num" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {data?.gender?.total || 0}
            </Box>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
